import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = currentCategory?.name || slug || '全部';

  const filteredComics = slug
    ? comics.filter((c) => c.category === slug)
    : comics;

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'latest') {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }
    if (sortBy === 'rating') {
      return b.rating - a.rating;
    }
    return b.views - a.views;
  });

  const totalPages = Math.ceil(sortedComics.length / pageSize);
  const paginatedComics = sortedComics.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  useEffect(() => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug, sortBy]);

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - 汗汗漫画`,
    description: `汗汗漫画${categoryName}漫画分类，汇集精选${categoryName}韩国漫画资源，免费在线畅读。`,
    url: `https://hanhancomic.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '汗汗漫画',
      url: 'https://hanhancomic.com',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${categoryName}漫画` },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryJsonLd) }}
      />

      <main>
        {/* Category Header */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">
              {categoryName}漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredComics.length} 部作品
            </p>

            {/* Category Tabs */}
            <div className="flex flex-wrap items-center gap-2 mt-6">
              <Link
                to="/category/all"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  !slug || slug === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-700 hover:bg-background-200'
                }`}
              >
                全部
              </Link>
              {categories.filter((c) => c.id !== 'all').map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Sort & Results */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Sort Bar */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">排序：</span>
                <button
                  onClick={() => setSortBy('popular')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    sortBy === 'popular'
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  最受欢迎
                </button>
                <button
                  onClick={() => setSortBy('latest')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    sortBy === 'latest'
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  最近更新
                </button>
                <button
                  onClick={() => setSortBy('rating')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    sortBy === 'rating'
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  最高评分
                </button>
              </div>
            </div>

            {/* Comic Grid */}
            {paginatedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {paginatedComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    title={comic.title}
                    author={comic.author}
                    cover={comic.cover}
                    categoryName={comic.categoryName}
                    categorySlug={comic.category}
                    rating={comic.rating}
                    views={comic.views}
                    chapters={comic.chapters}
                    status={comic.status}
                    updatedAt={comic.updatedAt}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-4xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">暂无相关漫画</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      currentPage === page
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}